import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { meta } from "@/lib/statusMeta";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Clock, CheckCircle2, Activity, TimerReset, AlertTriangle } from "lucide-react";

export default function Insights() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/analytics").then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  if (d === null) return <Layout><div className="py-24 text-center text-muted-foreground mono text-sm animate-pulse">Crunching numbers…</div></Layout>;
  if (d === false) return <Layout><div className="py-24 text-center text-muted-foreground">Could not load insights.</div></Layout>;

  const chartData = d.stages.map((s) => ({ ...s, name: s.label }));
  const allZero = d.stages.every((s) => s.avg_days === 0);

  return (
    <Layout>
      <div className="mb-8">
        <p className="mono text-xs uppercase tracking-[0.2em] text-brand mb-2">Lab Performance</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Turnaround Insights</h1>
        <p className="text-muted-foreground text-sm mt-2">Average time film spends in the lab, and where orders slow down along the bench.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Kpi label="Avg Turnaround" value={d.avg_turnaround_days != null ? `${d.avg_turnaround_days}d` : "—"} icon={TimerReset} accent testid="kpi-turnaround" />
        <Kpi label="Orders Completed" value={d.completed_count} icon={CheckCircle2} color="#10B981" testid="kpi-completed" />
        <Kpi label="Active in Lab" value={d.active_count} icon={Activity} testid="kpi-active" />
      </div>

      <div className="bg-card border border-border rounded-xl p-6 sm:p-8" data-testid="stage-chart">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Clock className="w-5 h-5 text-brand" /> Average days per stage</h2>
          {d.slowest_stage && !allZero && (
            <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: meta(d.slowest_stage).color + "22", color: meta(d.slowest_stage).color }}>
              <AlertTriangle className="w-3.5 h-3.5" /> Bottleneck: {meta(d.slowest_stage).label}
            </span>
          )}
        </div>

        {d.stages.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Not enough order history yet.</p>
        ) : (
          <>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 30 }}>
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} unit="d" />
                  <YAxis type="category" dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} width={90} tickLine={false} axisLine={false} />
                  <Tooltip cursor={{ fill: "hsl(var(--secondary))" }}
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, color: "hsl(var(--foreground))" }}
                    formatter={(v) => [`${v} days`, "Avg"]} />
                  <Bar dataKey="avg_days" radius={[0, 6, 6, 0]} barSize={26}>
                    {chartData.map((e) => (
                      <Cell key={e.status} fill={e.status === d.slowest_stage ? meta(e.status).color : "hsl(var(--muted-foreground))"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {allZero && <p className="mono text-[11px] text-muted-foreground mt-4">Sample orders were seeded at the same moment, so stage durations read ~0. Real turnaround builds as staff scan orders through the stations over time.</p>}
          </>
        )}
      </div>
    </Layout>
  );
}

function Kpi({ label, value, icon: Icon, color, accent, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4" style={{ color: color || (accent ? "#6BAE7C" : "hsl(var(--muted-foreground))") }} />
      </div>
      <div className="font-heading text-4xl font-bold" style={{ color: color || undefined }}>{value}</div>
    </div>
  );
}
